import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import { SITE_INFO, BRAND_ASSETS, FEATURED_PROJECTS } from '../data';
import { getSiteSettings, getFeaturedProjects, getServiceAreas } from '../lib/cmsClient';

export default function HomePage() {
  const [siteSettings, setSiteSettings] = useState({});
  const [featuredProjects, setFeaturedProjects] = useState(FEATURED_PROJECTS);
  const [services, setServices] = useState([
    {
      title: 'Entreprenør og anlæg',
      desc: 'Jordarbejde, byggemodning, belægning, dræn, haveanlæg og gårdrenovering.',
      href: '/entreprenerservice',
      icon: 'fa-trowel-bricks',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/dumper-1.jpg'
    },
    {
      title: 'Kloak og klimasikring',
      desc: 'Autoriseret kloakarbejde, højvandslukker, rottespærre, faskiner, omfangsdræn og løsninger mod skybrud.',
      href: '/kloak-service',
      icon: 'fa-faucet-drip',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak111.png'
    },
    {
      title: 'Betonarbejde',
      desc: 'Fundamenter, terrændæk, støttemure, kældre, forskalling og armering.',
      href: '/betonarbejde',
      icon: 'fa-cubes-stacked',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-ny-grund.jpg'
    },
    {
      title: 'TV-inspektion og spuling',
      desc: 'Undersøgelse af kloakledninger, lokalisering af skader, kloakspuling og dokumentation.',
      href: '/tv-inspektion',
      icon: 'fa-video',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/3259.jpg'
    },
    {
      title: 'Materialer og genbrug',
      desc: 'På vores plads i Frederiksværk sælger vi grus, sand, muld og genbrugsmaterialer til private og erhverv.',
      href: '/bygningsaffald',
      icon: 'fa-recycle',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-brokker.jpg'
    },
    {
      title: 'Udlejning',
      desc: 'Udlejning af køreplader, gangbroer, byggepladshegn og andet materiel.',
      href: '/udlejning',
      icon: 'fa-truck-ramp-box',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/gangbro.jpg'
    }
  ]);

  useEffect(() => {
    async function loadCmsData() {
      try {
        const [settingsData, projectsData, servicesData] = await Promise.all([
          getSiteSettings(),
          getFeaturedProjects(),
          getServiceAreas()
        ]);
        if (settingsData && Object.keys(settingsData).length > 0) {
          setSiteSettings(settingsData);
        }
        if (projectsData && projectsData.length > 0) {
          setFeaturedProjects(projectsData);
        }
        if (servicesData && servicesData.length > 0) {
          setServices(servicesData);
        }
      } catch (err) {
        console.warn("Error loading CMS data for homepage:", err);
      }
    }
    loadCmsData();
  }, []);

  const heroPoster = siteSettings.hero_image?.image || "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/imgi-9-dsc4768b-edited-1.jpg";
  const aboutImage = siteSettings.about_image?.image || "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kts-maskine-firmabil.webp";

  return (
    <div>
      <PageSEO
        title="Entreprenør og autoriseret kloakmester i Nordsjælland | KTS-Entreprise A/S"
        description="Vi udfører jord-, kloak- og betonarbejde for private, erhverv og offentlige bygherrer i Nordsjælland og omegn. Hos os er kvalitet, tillid og service en naturlig del af arbejdet."
        ogTitle="Entreprenør og autoriseret kloakmester i Nordsjælland | KTS-Entreprise A/S"
        ogDescription="Vi udfører jord-, kloak- og betonarbejde for private, erhverv og offentlige bygherrer i Nordsjælland og omegn."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="index"
      />

      {/* 2. Hero Section */}
      <section className="relative bg-stone-950 text-white overflow-hidden border-b border-stone-800 min-h-[85vh] lg:min-h-[760px] flex items-center">
        {/* Background Image: kts-forside-hero.webp */}
        <div className="absolute inset-0 z-0 overflow-hidden bg-stone-950">
          <img
            src="https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kts-forside-hero.webp"
            alt="Fire KTS-Entreprise medarbejdere ved gravemaskine og vand"
            fetchPriority="high"
            className="w-full h-full object-cover object-[75%_center] sm:object-[70%_center] lg:object-[65%_center]"
          />

          {/* Contrast overlays preserving readability on left side while keeping people, excavator & water visible */}
          <div className="absolute inset-0 z-10 bg-black/40 lg:bg-black/25 pointer-events-none"></div>
          <div className="absolute inset-0 z-20 bg-gradient-to-r from-stone-950/95 via-stone-950/75 via-40% to-transparent lg:from-stone-950/90 lg:via-stone-950/50 lg:via-45% lg:to-transparent pointer-events-none"></div>
          <div className="absolute inset-0 z-20 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/30 pointer-events-none"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-30 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 lg:pt-44 pb-16 sm:pb-20 lg:pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-4 rounded-xs">
              <i className="fa-solid fa-shield-halved text-[#0072c0]"></i>
              <span>Autoriseret kloakmester &amp; Entreprenør</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white leading-tight font-heading mb-6 drop-shadow-md">
              Entreprenør og autoriseret kloakmester i Nordsjælland
            </h1>

            <p className="text-stone-100 text-base sm:text-lg lg:text-xl leading-relaxed mb-8 max-w-2xl font-normal drop-shadow-sm">
              Vi udfører jord-, kloak- og betonarbejde for private, erhverv og offentlige bygherrer. Hos os er kvalitet, tillid og service en naturlig del af arbejdet.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2.5 bg-[#0072c0] hover:bg-[#005b99] active:bg-[#00487a] text-white font-heading font-bold uppercase tracking-wider px-6 py-3.5 sm:px-8 sm:py-4 text-xs sm:text-sm lg:text-base transition-all duration-200 shadow-lg cursor-pointer rounded-xs"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>

              <a
                href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2.5 bg-stone-900/90 hover:bg-black text-white font-heading font-bold uppercase tracking-wider px-5 py-3.5 sm:px-7 sm:py-4 text-xs sm:text-sm lg:text-base transition-all duration-200 border border-stone-700 shadow-lg backdrop-blur-xs rounded-xs"
              >
                <i className="fa-solid fa-phone text-[#0072c0]" aria-hidden="true"></i>
                <span className="font-mono">{SITE_INFO.mainPhone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Afsnit: KTS-Entreprise – lokalt forankret siden 1986 */}
      <section className="py-14 sm:py-20 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="border-l-4 border-[#0072c0] pl-4">
                <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] block mb-1">
                  Om virksomheden
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
                  KTS-Entreprise – lokalt forankret siden 1986
                </h2>
              </div>

              <div className="space-y-4 text-stone-700 leading-relaxed text-base sm:text-lg">
                <p>
                  KTS-Entreprise blev grundlagt i 1986 som vognmandsfirma. I slutningen af 1990’erne udviklede virksomheden sig til den entreprenørvirksomhed, vi er i dag.
                </p>
                <p>
                  Vi udfører entreprenør-, kloak-, jord- og betonarbejde for private, erhverv og offentlige bygherrer i Nordsjælland og omegn. Virksomheden beskæftiger cirka 20 medarbejdere inden for bygge og anlæg, værksted og administration.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs sm:text-sm transition-colors rounded-xs shadow-xs"
                >
                  <span>Læs mere om KTS-Entreprise</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-900 border border-stone-300 font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs sm:text-sm transition-colors rounded-xs"
                >
                  <span>Kontakt os</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden border border-stone-200 shadow-sm rounded-xs aspect-4/3 bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kts-maskine-firmabil.webp"
                  alt="KTS-Entreprise gravemaskine og firmabil på arbejdsplads"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Fagområder: "Det kan vi hjælpe med" */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="border-l-4 border-[#0072c0] pl-4 pb-2 mb-10 max-w-3xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Det kan vi hjælpe med
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Vi udfører både mindre serviceopgaver og større entrepriser for private, erhverv og offentlige bygherrer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((area) => (
              <div
                key={area.title}
                className="bg-white border border-stone-200 hover:border-[#0072c0]/60 hover:shadow-md transition-all duration-200 flex flex-col justify-between rounded-xs group"
              >
                <div className="relative aspect-16/10 bg-stone-100 overflow-hidden border-b border-stone-200">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${area.image.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                    alt={area.alt || area.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2.5 text-[#0072c0] mb-2">
                      <i className={`fa-solid ${area.icon} text-base`} aria-hidden="true"></i>
                      <h3 className="font-heading font-bold text-lg text-stone-900 uppercase tracking-tight">
                        {area.title}
                      </h3>
                    </div>
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed mt-2">
                      {area.desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-stone-100">
                    <Link
                      to={area.href}
                      className="inline-flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] group-hover:text-[#005b99] transition-colors"
                    >
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Projektafsnit: "Udvalgte projekter" (3 projekter) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="border-l-4 border-[#0072c0] pl-4 pb-2 mb-10 max-w-3xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Udvalgte projekter
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Se eksempler på nogle af de opgaver, vi har udført inden for entreprenørarbejde, kloak, klimasikring og beton.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {featuredProjects.slice(0, 3).map((proj) => (
              <div
                key={proj.id || proj.title}
                className="bg-stone-50 border border-stone-200 flex flex-col justify-between rounded-xs overflow-hidden group hover:border-[#0072c0]/60 transition-all duration-200"
              >
                <div className="relative aspect-16/10 bg-stone-200 overflow-hidden">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${proj.image.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                    alt={proj.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {proj.category && (
                    <span className="absolute top-3 left-3 bg-stone-900/85 text-white text-[11px] font-heading font-bold uppercase tracking-wider px-2.5 py-1 rounded-xs">
                      {proj.category}
                    </span>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-base sm:text-lg text-stone-900 uppercase tracking-tight mb-2">
                      {proj.title}
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                      {proj.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-stone-200">
                    <Link
                      to={proj.href || '/projekter'}
                      className="inline-flex items-center justify-center w-full gap-2 bg-stone-900 hover:bg-[#0072c0] text-white font-heading font-bold uppercase tracking-wider py-2.5 px-4 text-xs transition-colors rounded-xs"
                    >
                      <span>Se projektet</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/projekter"
              className="inline-flex items-center gap-2.5 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-8 py-3.5 text-xs sm:text-sm transition-colors rounded-xs shadow-xs"
            >
              <span>Se flere projekter</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>

        </div>
      </section>

      {/* 7. Kontaktområde */}
      <section className="py-16 sm:py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            
            {/* Left: Contact Info & Opening Hours */}
            <div className="lg:col-span-5 space-y-6">
              <div className="border-l-4 border-[#0072c0] pl-4">
                <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] block mb-1">
                  Uforpligtende dialog
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
                  Kontakt KTS-Entreprise
                </h2>
              </div>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Har du brug for hjælp til entreprenør-, kloak-, jord- eller betonarbejde, er du velkommen til at kontakte os for en uforpligtende snak om opgaven.
              </p>

              <div className="bg-white border border-stone-200 p-6 space-y-4 rounded-xs shadow-xs">
                <div>
                  <span className="text-xs text-stone-500 font-bold uppercase block">Hovednummer</span>
                  <a
                    href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                    className="text-2xl font-black text-stone-900 hover:text-[#0072c0] font-mono tracking-tight transition-colors block mt-0.5"
                  >
                    {SITE_INFO.mainPhone}
                  </a>
                </div>

                <div className="pt-2 border-t border-stone-100">
                  <span className="text-xs text-stone-500 font-bold uppercase block">Adresse</span>
                  <p className="text-sm font-semibold text-stone-900">Industrivej 3, 3300 Frederiksværk</p>
                </div>

                <div className="pt-2 border-t border-stone-100">
                  <span className="text-xs text-stone-500 font-bold uppercase block mb-1">Åbningstider</span>
                  <p className="text-xs text-stone-700"><strong>Mandag–torsdag:</strong> 6.30–15.30</p>
                  <p className="text-xs text-stone-700"><strong>Fredag:</strong> 6.30–15.00</p>
                </div>
              </div>
            </div>

            {/* Right: Formular */}
            <div className="lg:col-span-7">
              <WebContactForm
                title="Kontakt KTS-Entreprise"
                subtitle="Udfyld formularen herunder, så vender vi hurtigt tilbage."
                submitText="Send forespørgsel"
                showAddress={true}
                showTaskTypeSelect={true}
                taskTypeOptions={[
                  "Entreprenør og anlæg",
                  "Kloak og klimasikring",
                  "Betonarbejde",
                  "TV-inspektion og spuling",
                  "Materialer og genbrug",
                  "Udlejning af materiel",
                  "Andet"
                ]}
                showFileUpload={true}
                fileLabel="Mulighed for at vedhæfte billeder (valgfrit)"
              />
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
